import { useEffect, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useLogout, clearUserSession } from '@zendia/frontend-shared'
import { axiosRequest } from '../../utilities/axiosClient'
import { Alert, AlertVariants } from '../../components/Alert'
import { Button, ButtonVariants } from '../../components/Button'
import { useAllTasksFiltersStore } from '../../components/Task/useAllTasksFiltersStore'

export const Route = createFileRoute('/_authenticated/logout')({
  component: LogoutComponent,
})

function LogoutComponent() {
  const navigate = useNavigate()
  const logoutMutation = useLogout(axiosRequest)
  const queryClient = useQueryClient()
  const [hasFailed, setHasFailed] = useState(false)
  // StrictMode mounts effects twice in development, and logging out twice would make the
  // second call fail against an already-revoked session and show a spurious error.
  const hasStarted = useRef(false)

  const performLogout = async () => {
    setHasFailed(false)

    try {
      await logoutMutation.mutateAsync()
    } catch {
      // Signing out is the server's job: it is what deletes the refresh tokens and clears
      // the cookie. Clearing this tab and redirecting anyway would show the user a login
      // screen while their session was still live and restorable on the next reload -
      // worst of all on the shared machine that made them sign out in the first place.
      setHasFailed(true)
      return
    }

    // The stores and the query cache both outlive a sign-out, and code that reads them to
    // decide where to send the user would otherwise act on the session just ended.
    clearUserSession()
    useAllTasksFiltersStore.getState().reset()
    queryClient.clear()
    navigate({ to: '/login' })
  }

  useEffect(() => {
    if (hasStarted.current) return
    hasStarted.current = true
    void performLogout()
    // Deliberately runs once on mount; performLogout closes over what it needs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (hasFailed) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="max-w-md w-full px-4">
          <Alert type={AlertVariants.error}>
            <div className="w-full text-center">
              We couldn&apos;t sign you out. You are still signed in - please try again.
            </div>
          </Alert>
          <Button
            onClick={() => void performLogout()}
            intent={ButtonVariants.primary}
            size={ButtonVariants.large}
            className="w-full mt-6"
            disabled={logoutMutation.isPending}
          >
            {logoutMutation.isPending ? 'Signing out...' : 'Try Again'}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <p className="text-lg">Logging out...</p>
      </div>
    </div>
  )
}
